import { useEffect, useState } from 'react';

const weekdayLabels = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];

const BulkUpdateModal = ({ open, onClose, onSubmit, range, defaults = {} }) => {
  const [start, setStart] = useState(range?.from || '');
  const [end, setEnd] = useState(range?.to || '');
  const [status, setStatus] = useState(defaults.status || 'available');
  const [price, setPrice] = useState(defaults.price_override || '');
  const [minNights, setMinNights] = useState(defaults.min_nights_override || '');
  const [note, setNote] = useState('');
  const [weekdays, setWeekdays] = useState([]);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    if (open) {
      setStart(range?.from || '');
      setEnd(range?.to || '');
      setStatus(defaults.status || 'available');
      setPrice(defaults.price_override || '');
      setMinNights(defaults.min_nights_override || '');
      setNote(defaults.note || '');
      setWeekdays([]);
      setError('');
    }
  }, [open, range, defaults]);

  const toggleWeekday = (idx) => {
    setWeekdays((prev) => (prev.includes(idx) ? prev.filter((d) => d !== idx) : [...prev, idx]));
  };

  const handleSubmit = async () => {
    if (!start) {
      setError('Start date is required.');
      return;
    }
    if (!end) {
      setError('End date is required.');
      return;
    }
    if (end < start) {
      setError('End date cannot be before start date.');
      return;
    }
    if (!status) {
      setError('Status is required.');
      return;
    }
    setSubmitting(true);
    setError('');
    try {
      await onSubmit?.({
        from: start,
        to: end || start,
        status,
        price_override: price,
        min_nights_override: minNights,
        note,
        weekdays,
      });
      onClose?.();
    } catch (err) {
      setError(err.message || 'Failed to update availability');
    } finally {
      setSubmitting(false);
    }
  };

  if (!open) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-slate-900/40 p-4">
      <div className="w-full max-w-2xl rounded-2xl bg-white shadow-xl">
        <div className="flex items-center justify-between border-b border-slate-200 px-6 py-4">
          <div>
            <div className="text-base font-semibold text-slate-900">Bulk update</div>
            <div className="text-xs text-slate-500">Apply status/price to a date range</div>
          </div>
          <button
            type="button"
            className="rounded-full p-2 text-slate-500 hover:bg-slate-100"
            onClick={() => onClose?.()}
          >
            X
          </button>
        </div>
        <div className="grid gap-4 px-6 py-4 sm:grid-cols-2">
          <div>
            <label className="text-xs font-semibold text-slate-600">Start date</label>
            <input
              type="date"
              className="mt-1 w-full rounded-lg border border-slate-200 px-3 py-2 text-sm"
              value={start}
              onChange={(e) => setStart(e.target.value)}
            />
          </div>
          <div>
            <label className="text-xs font-semibold text-slate-600">End date</label>
            <input
              type="date"
              className="mt-1 w-full rounded-lg border border-slate-200 px-3 py-2 text-sm"
              value={end}
              onChange={(e) => setEnd(e.target.value)}
            />
          </div>
          <div>
            <label className="text-xs font-semibold text-slate-600">Status</label>
            <select
              className="mt-1 w-full rounded-lg border border-slate-200 px-3 py-2 text-sm"
              value={status}
              onChange={(e) => setStatus(e.target.value)}
            >
              <option value="available">Available</option>
              <option value="blocked">Blocked</option>
            </select>
          </div>
          <div>
            <label className="text-xs font-semibold text-slate-600">Price override (optional)</label>
            <input
              type="number"
              className="mt-1 w-full rounded-lg border border-slate-200 px-3 py-2 text-sm"
              value={price}
              onChange={(e) => setPrice(e.target.value)}
              placeholder="Leave blank to use base price"
            />
          </div>
          <div>
            <label className="text-xs font-semibold text-slate-600">Min nights override</label>
            <input
              type="number"
              min="1"
              className="mt-1 w-full rounded-lg border border-slate-200 px-3 py-2 text-sm"
              value={minNights}
              onChange={(e) => setMinNights(e.target.value)}
              placeholder="Optional"
            />
          </div>
          <div>
            <label className="text-xs font-semibold text-slate-600">Note</label>
            <textarea
              className="mt-1 w-full rounded-lg border border-slate-200 px-3 py-2 text-sm"
              rows={3}
              value={note}
              onChange={(e) => setNote(e.target.value)}
              placeholder="Optional note"
            />
          </div>
          <div className="sm:col-span-2">
            <div className="text-xs font-semibold text-slate-600">Apply on weekdays</div>
            <div className="mt-2 flex flex-wrap gap-2">
              {weekdayLabels.map((label, idx) => {
                const active = weekdays.includes(idx);
                return (
                  <button
                    key={label}
                    type="button"
                    className={[
                      'rounded-full border px-3 py-1 text-sm',
                      active ? 'border-indigo-200 bg-indigo-50 text-indigo-700' : 'border-slate-200 bg-white text-slate-700',
                    ].join(' ')}
                    onClick={() => toggleWeekday(idx)}
                  >
                    {label}
                  </button>
                );
              })}
            </div>
          </div>
        </div>
        {error && <div className="px-6 pb-2 text-sm text-rose-600">{error}</div>}
        <div className="flex items-center justify-end gap-2 border-t border-slate-200 px-6 py-4">
          <button
            type="button"
            className="rounded-lg border border-slate-200 px-4 py-2 text-sm font-semibold text-slate-700 hover:bg-slate-50"
            onClick={() => onClose?.()}
            disabled={submitting}
          >
            Cancel
          </button>
          <button
            type="button"
            className="rounded-lg bg-emerald-600 px-4 py-2 text-sm font-semibold text-white hover:bg-emerald-700 disabled:opacity-60"
            onClick={handleSubmit}
            disabled={submitting}
          >
            {submitting ? 'Applying...' : 'Apply updates'}
          </button>
        </div>
      </div>
    </div>
  );
};

export default BulkUpdateModal;
